import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Area, AreaChart, CartesianGrid, Line, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '@/lib/format';

interface TrancheData {
  month_index: number;
  expected_collections: number;
  ci_low: number;
  ci_high: number;
}

interface ConfidenceChartProps {
  data: TrancheData[];
  confidence?: number;
}

export const ConfidenceChart = ({ data, confidence = 90 }: ConfidenceChartProps) => {
  const chartData = data.map(d => ({
    month: d.month_index,
    p50: d.expected_collections,
    p10: d.ci_low,
    p90: d.ci_high,
  }));

  const chartConfig = {
    p10: {
      label: 'P10 (Low)',
      color: 'hsl(var(--chart-1))',
    },
    p50: {
      label: 'P50 (Expected)',
      color: 'hsl(var(--chart-2))',
    },
    p90: {
      label: 'P90 (High)',
      color: 'hsl(var(--chart-3))',
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Expected Collections with Confidence Interval</CardTitle>
        <CardDescription>
          {confidence}% confidence band showing expected monthly collections
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[400px]">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="month" 
              label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
              className="text-xs"
            />
            <YAxis 
              label={{ value: 'Collections ($)', angle: -90, position: 'insideLeft' }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              className="text-xs"
            />
            <ChartTooltip 
              content={
                <ChartTooltipContent 
                  formatter={(value, name) => [
                    formatCurrency(Number(value)),
                    name === 'p10' ? 'P10 (Low)' : name === 'p50' ? 'P50 (Expected)' : 'P90 (High)'
                  ]}
                />
              }
            />
            <defs>
              <linearGradient id="ciArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="p90"
              stackId="1"
              stroke="hsl(var(--chart-1))"
              fill="url(#ciArea)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <Area
              type="monotone"
              dataKey="p10"
              stackId="1"
              stroke="hsl(var(--chart-1))"
              fill="transparent"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <Line
              type="monotone"
              dataKey="p50"
              stroke="hsl(var(--chart-2))"
              strokeWidth={3}
              dot={{ fill: 'hsl(var(--chart-2))', r: 3 }}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
